import type { FC, ReactNode } from 'react'
import type { Profile } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { buttonVariants } from '@/components/ui/button'

interface CtaSectionProps {
    profile: Profile | null
}

export const CtaSection: FC<CtaSectionProps> = ({ profile }): ReactNode => {
    if (!profile?.cta_heading) return null

    return (
        <section id='start-a-project' className='py-20 sm:py-28'>
            <Container>
                <div className='relative overflow-hidden rounded-3xl bg-primary px-6 py-14 text-primary-foreground sm:px-12 sm:py-20 lg:px-16'>
                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute -right-24 -top-24 size-80 rounded-full border border-primary-foreground/10'
                    />

                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute -right-8 -top-8 size-48 rounded-full border border-primary-foreground/10'
                    />

                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute right-16 top-16 size-3 rounded-full bg-brand'
                    />

                    <h2
                        data-mdlfy-cms={`profile:${profile.id}:cta_heading`}
                        className='relative max-w-2xl font-heading text-5xl leading-[1.02] sm:text-6xl'
                    >
                        {profile.cta_heading}
                    </h2>

                    {profile.cta_text && (
                        <p
                            data-mdlfy-cms={`profile:${profile.id}:cta_text`}
                            className='relative mt-6 max-w-xl text-lg leading-relaxed text-primary-foreground/75'
                        >
                            {profile.cta_text}
                        </p>
                    )}

                    <Link
                        href='/contact'
                        className={cn(
                            buttonVariants({ size: 'xl' }),
                            'relative mt-10 bg-primary-foreground text-primary hover:bg-primary-foreground/90 focus-visible:ring-primary-foreground/50'
                        )}
                    >
                        Start a conversation

                        <ArrowRight data-icon='inline-end' />
                    </Link>
                </div>
            </Container>
        </section>
    )
}
